import type {
	AbortEvent,
	ExperienceEvent,
	ExperienceTracker,
	FailEvent,
	StartEvent,
	SuccessEvent,
} from '@atlassian/experience-tracker';

import { EXPERIENCE_FORWARDING_MODULE_NAME, type ExperienceForwardingModule } from './channel';
import { requireModule } from './requireModule';

/**
 * Entry point method will be used within confluence-frontend iframe to forward experience tracker events
 * In parent window, the module will be set up as example:
 *
 * host.defineModule(EXPERIENCE_TRACKER_FORWARDING_MODULE.name, {
 *     start: () => { // Delegate to parent window to execute experienceTracker.start },
 *     abort: () => { // Delegate to parent window to execute experienceTracker.abort },
 *     succeed: () => { // Delegate to parent window to execute experienceTracker.succeed },
 *     fail: () => { // Delegate to parent window to execute experienceTracker.fail },
 * })
 */

export const crossDomainExperienceForwarding = (
	fromExperienceTracker: Pick<ExperienceTracker, 'subscribe'>,
) => {
	const module = requireModule<ExperienceForwardingModule>(EXPERIENCE_FORWARDING_MODULE_NAME);

	return module
		? fromExperienceTracker.subscribe(({ action, ...options }: ExperienceEvent) => {
				switch (action) {
					case 'taskAbort':
						module.abort({
							...(options as Omit<AbortEvent, 'action'>),
							checkForTimeout: false, // disallow `taskAbort` to be converted into `taskFail`
						});
						break;
					case 'taskFail':
						const {
							error: { message, name },
						} = options as Omit<FailEvent, 'action'>;

						module.fail({
							...options,
							// Transform the error object as pure object so that it can be transported back to EP on AFP through `window.postMessage`
							// This is because error is instance of Error class, and non-primitive data is not supported to transport
							// https://developer.mozilla.org/en-US/docs/Web/API/Web_Workers_API/Structured_clone_algorithm
							error: {
								message,
								name,
							},
						} as Omit<FailEvent, 'action'>);
						break;
					case 'taskStart':
						module.start(options as Omit<StartEvent, 'action'>);
						break;
					case 'taskSuccess':
						module.succeed(options as Omit<SuccessEvent, 'action'>);
						break;
				}
			})
		: undefined;
};
